import React, { useState } from "react";
import { EuiText, EuiTitle, EuiHorizontalRule } from "@elastic/eui";
import { ArrowRight } from "lucide-react";
import { useAnketaI18n } from "src/internationalization";

interface Partner {
  id: string;
  name: string;
  logo: string;
  description: string;
  website: string;
}

interface PartnersProps {
  columns?: number;
}

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  subtleBg: "var(--ak-surface-2)",
  border: "var(--ak-border)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  radius: 12,
};

const PartnerCard: React.FC<{
  partner: Partner;
  visitLabel: string;
}> = ({ partner, visitLabel }) => {
  const [linkHovered, setLinkHovered] = useState(false);

  // Only the "visit" link is clickable, so only it reacts to hover - a
  // card-level (or logo) hover would imply the whole card is a link.
  return (
    <div
      title={partner.description}
      style={{
        background: C.cardBg,
        border: `1px solid ${C.border}`,
        borderRadius: C.radius,
        padding: "28px 24px 22px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        boxShadow: "0 1px 4px var(--ak-shadow)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 18,
        }}
      >
        <img
          src={partner.logo}
          alt={partner.name}
          style={{
            maxWidth: 160,
            maxHeight: 64,
            objectFit: "contain",
            filter: "grayscale(80%)",
            opacity: 0.75,
          }}
        />
      </div>

      <div
        style={{
          fontSize: "0.88rem",
          fontWeight: 700,
          color: C.accent,
          textAlign: "center",
          marginBottom: 6,
        }}
      >
        {partner.name}
      </div>

      <div
        style={{
          fontSize: "0.78rem",
          color: C.textMuted,
          textAlign: "center",
          lineHeight: 1.5,
          marginBottom: 18,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {partner.description}
      </div>

      <div
        style={{
          height: 1,
          width: "100%",
          background: C.border,
          marginBottom: 14,
        }}
      />

      <a
        href={partner.website}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setLinkHovered(true)}
        onMouseLeave={() => setLinkHovered(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: "0.8rem",
          fontWeight: 600,
          color: linkHovered ? C.accent : C.textMuted,
          textDecoration: "none",
          transition: "color 0.18s",
        }}
      >
        {visitLabel}
        <ArrowRight
          size={13}
          style={{
            transform: linkHovered ? "translateX(3px)" : "none",
            transition: "transform 0.18s",
          }}
        />
      </a>
    </div>
  );
};

export const AutonubilPartnersSection: React.FC<PartnersProps> = ({
  columns = 5,
}) => {
  const { translate } = useAnketaI18n();
  const i18nPrefix = "anketa.autonubil.home.partners";

  const partners: Partner[] = [
    /*
    {
      id: "linux-foundation",
      name: translate(`${i18nPrefix}.linux-foundation.name`),
      logo: "https://www.linuxfoundation.org/hubfs/LF-Europe-logo-color.svg",
      description: translate(`${i18nPrefix}.linux-foundation.description`),
      website: "https://linuxfoundation.eu/",
    },
    {
      id: "cncf",
      name: translate(`${i18nPrefix}.cncf.name`),
      logo: "https://www.cncf.io/wp-content/uploads/2022/07/cncf-color-bg.svg",
      description: translate(`${i18nPrefix}.cncf.description`),
      website: "https://www.cncf.io",
    },
    {
      id: "opennebula",
      name: translate(`${i18nPrefix}.opennebula.name`),
      logo: "https://opennebula.io/wp-content/uploads/2020/04/opennebula_cloud_logo_white_bg.svg",
      description: translate(`${i18nPrefix}.opennebula.description`),
      website: "https://opennebula.io/",
    },
    {
      id: "server-factory",
      name: translate(`${i18nPrefix}.server-factory.name`),
      logo: "https://serverfactory.com/media/logo/stores/1/Version_12_PNG_300ppi_.png",
      description: translate(`${i18nPrefix}.server-factory.description`),
      website: "https://www.serverfactory.com",
    },
    */
    {
      id: "medocino",
      name: translate(`${i18nPrefix}.medocino.name`, {
        defaultMessage: "medocino Hamburg GmbH",
      }),
      logo: "https://medocino.net/wp-content/uploads/2020/09/Logo-neu-768x204.png",
      description: translate(`${i18nPrefix}.medocino.description`),
      website: "https://medocino.net/",
    },
  ];

  return (
    <div
      id="partners"
      style={{
        background: C.pageBg,
        padding: "72px clamp(24px, 5vw, 48px)",
        borderTop: `1px solid ${C.border}`,
        fontFamily: C.sans,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.accent,
              marginBottom: 10,
            }}
          >
            {translate(`${i18nPrefix}.eyebrow`, {
              defaultMessage: "Trusted partners",
            } as any)}
          </div>

          <div style={{ width: "fit-content", maxWidth: "100%", margin: "0 auto 20px" }}>
            <EuiTitle size="l">
              <h2
                style={{
                  color: C.textPrimary,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  marginBottom: 0,
                }}
              >
                {translate(`${i18nPrefix}.title`)}
              </h2>
            </EuiTitle>
            <div
              style={{
                width: "100%",
                height: 3,
                marginTop: 14,
                background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
                borderRadius: 2,
              }}
            />
          </div>

          <EuiText color="subdued" style={{ maxWidth: 640, margin: "0 auto" }}>
            <p style={{ fontSize: "0.97rem", lineHeight: 1.75 }}>
              {translate(`${i18nPrefix}.subtitle`)}
            </p>
          </EuiText>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 20,
            justifyContent: "center",
          }}
        >
          {partners.map((partner) => (
            <div
              key={partner.id}
              style={{
                flex: `0 1 ${Math.floor(1100 / columns)}px`,
                minWidth: Math.floor(1100 / columns),
                maxWidth: 360,
              }}
            >
              <PartnerCard
                partner={partner}
                visitLabel={translate(`${i18nPrefix}.action.visit`)}
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: 48 }}>
          <EuiHorizontalRule margin="none" />
          <div
            style={{
              marginTop: 24,
              textAlign: "center",
            }}
          >
            <EuiText color="subdued" size="s">
              <p style={{ margin: 0 }}>
                {translate(`${i18nPrefix}.action.request.title`)}{" "}
                <a
                  href="#contact"
                  style={{
                    color: C.accent,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.textDecoration = "none")
                  }
                >
                  {translate(`${i18nPrefix}.action.request.body`)}
                </a>
              </p>
            </EuiText>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutonubilPartnersSection;
